import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import api from "@/lib/api";
import { FORMS } from "@/config/forms";
import { fmtMarks } from "@/lib/scoring";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useSortableData, SortableHead } from "@/hooks/useSortableData";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Check, X, Eye, Inbox } from "lucide-react";
import { toast } from "sonner";

export default function PendingApprovals() {
  const { hasPerm } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectItem, setRejectItem] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [viewItem, setViewItem] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/assessments/pending").then(({ data }) => setItems(data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    try { await api.put(`/assessments/${id}/status`, { status: "approved" }); toast.success("Approved"); setItems((s) => s.filter((x) => x.id !== id)); window.dispatchEvent(new Event("pending-refresh")); }
    catch { toast.error("Action failed"); }
  };
  const doReject = async () => {
    const id = rejectItem.id;
    try {
      await api.put(`/assessments/${id}/status`, { status: "rejected", reason: rejectReason });
      toast.success("Rejected");
      setRejectItem(null); setRejectReason("");
      setItems((s) => s.filter((x) => x.id !== id));
      window.dispatchEvent(new Event("pending-refresh"));
    } catch { toast.error("Action failed"); }
  };

  const catTitle = (k) => FORMS[k]?.title || k;
  const viewForm = useMemo(() => (viewItem ? FORMS[viewItem.category] : null), [viewItem]);
  const sort = useSortableData(items);
  const sorted = sort.sorted;

  if (!hasPerm("assessments.approve")) return <Navigate to="/" replace />;

  return (
    <div data-testid="pending-approvals-page">
      <div className="mb-6">
        <p className="text-xs uppercase tracking-[0.2em] text-emerald-600 font-medium">Approvals</p>
        <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight">Pending Approvals</h1>
        <p className="text-sm text-slate-500 mt-1">{items.length} submission(s) awaiting your review across all categories.</p>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">S.No</TableHead>
                <SortableHead column="category" sort={sort}>Category</SortableHead>
                <SortableHead column="data.municipality.district" sort={sort}>District</SortableHead>
                <SortableHead column="data.municipality.name" sort={sort}>Municipality</SortableHead>
                <SortableHead column="data.assessor_name" sort={sort}>Assessor</SortableHead>
                <SortableHead column="score" sort={sort}>Marks</SortableHead>
                <SortableHead column="created_by_name" sort={sort}>Submitted By</SortableHead>
                <SortableHead column="created_at" sort={sort}>Date</SortableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={9} className="text-center py-10 text-slate-400">Loading...</TableCell></TableRow>
              ) : sorted.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-12">
                    <Inbox className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-500">No pending submissions. You're all caught up.</p>
                  </TableCell>
                </TableRow>
              ) : (
                sorted.map((it, idx) => (
                  <TableRow key={it.id} className="hover:bg-slate-50" data-testid={`pending-row-${it.id}`}>
                    <TableCell className="text-slate-500">{idx + 1}</TableCell>
                    <TableCell className="font-medium text-slate-800">{catTitle(it.category)}</TableCell>
                    <TableCell className="capitalize">{(it.data?.municipality?.district || "—").toLowerCase()}</TableCell>
                    <TableCell>{it.data?.municipality?.name || "—"}</TableCell>
                    <TableCell>{it.data?.assessor_name || "—"}</TableCell>
                    <TableCell>{it.score != null ? `${fmtMarks(it.score)} / ${fmtMarks(it.max_score)}` : "—"}</TableCell>
                    <TableCell>{it.created_by_name}</TableCell>
                    <TableCell>{new Date(it.created_at).toLocaleDateString()}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button variant="ghost" size="sm" onClick={() => setViewItem(it)} data-testid={`pending-view-${it.id}`}><Eye className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => approve(it.id)} title="Approve" data-testid={`pending-approve-${it.id}`}><Check className="h-4 w-4 text-emerald-600" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => { setRejectItem(it); setRejectReason(""); }} title="Reject" data-testid={`pending-reject-${it.id}`}><X className="h-4 w-4 text-red-500" /></Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={!!rejectItem} onOpenChange={(o) => !o && setRejectItem(null)}>
        <DialogContent data-testid="pending-reject-dialog">
          <DialogHeader><DialogTitle className="font-heading">Reject Assessment</DialogTitle></DialogHeader>
          <div className="space-y-2 py-2">
            <label className="text-sm text-slate-600">Reason for rejection (visible to the assessor)</label>
            <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Explain what needs to be corrected..." rows={4} data-testid="pending-reject-reason" />
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setRejectItem(null)} data-testid="pending-reject-cancel">Cancel</Button>
            <Button onClick={doReject} className="bg-red-500 hover:bg-red-600 text-white" data-testid="pending-reject-confirm">Reject</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewItem} onOpenChange={(o) => !o && setViewItem(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="pending-view-dialog">
          <DialogHeader><DialogTitle className="font-heading">{viewForm?.title}</DialogTitle></DialogHeader>
          {viewItem && viewForm && (
            <div className="space-y-4">
              {viewForm.fields.map((f) => {
                const val = viewItem.data?.[f.name];
                let disp = "—";
                if (val !== undefined && val !== null && val !== "") {
                  if (f.type === "municipality") disp = [val.district, val.name, val.type].filter(Boolean).join(" / ");
                  else if (f.type === "geotag") disp = `${val.lat}, ${val.lng}`;
                  else if (f.type === "checkbox") disp = (val || []).join(", ");
                  else if (f.type === "image") disp = "(image)";
                  else disp = val.toString();
                }
                return (
                  <div key={f.name} className="border-b border-slate-100 pb-3">
                    <p className="text-xs font-medium text-slate-500 mb-1">{f.label}</p>
                    <div className="text-sm text-slate-800 break-words">{disp}</div>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
